import React from "react";
import useAppliedFilters from "../Hooks/useAppliedFilters";
import {
  ACTIVE_TRAP_STATUS,
  selectComponentStyles,
} from "../../../../services/utils/globalFunctions";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "../../../../components/Cards/Card";
import { Button, Col, Row } from "reactstrap";
import ReactTooltip from "react-tooltip";
import DataTable from "react-data-table-component";
import useLoading from "../Hooks/useLoading";
import TableLoadingSkeleton from "../../../../components/ui/Loading/TableLoadingSkeleton";
import moment from "moment";
import xlsx from "json-as-xlsx";
import { ARMADILHA_INFO } from "../../../../constants/ArmadilhaConstant";
import { Select } from "../../../../components/Wrappers/SelectAll";
import { CircleHelp } from "lucide-react";

const useTrapsMaintenanceStageByTerritorializationsTable = (trapsList) => {
  const [
    maintenanceStageByTerritorializations,
    setMaintenanceStageByTerritorializations,
  ] = React.useState([]);
  const [
    maintenanceStageCumulativeByTerritorializations,
    setMaintenanceStageCumulativeByTerritorializations,
  ] = React.useState([]);
  const [selectedTableType, setSelectedTableType] = React.useState({
    label: "Exclusivo",
    value: "exclusive",
  });
  const { appliedFilters } = useAppliedFilters();
  const { isTrapsListLoading } = useLoading();

  React.useEffect(() => {
    if (
      appliedFilters.territorializations.length === 0 ||
      appliedFilters.trapType.trapTypeName !==
        "armadilhaDisseminadoraInseticida" ||
      trapsList.length === 0
    )
      return;

    const maintenanceStageByAreas = calculateMaintenanceStageByAreas(
      trapsList,
      appliedFilters.territorializations
    );

    const maintenanceStageByAreasCumulative =
      calculateMaintenanceStageByAreasCumulative(
        trapsList,
        appliedFilters.territorializations
      );

    setMaintenanceStageByTerritorializations(maintenanceStageByAreas);
    setMaintenanceStageCumulativeByTerritorializations(
      maintenanceStageByAreasCumulative
    );
  }, [appliedFilters, trapsList]);

  const calculateMaintenanceStageByAreas = (
    trapsList,
    territorializationsList
  ) => {
    const maintenanceStageByAreas = {};

    const generateMaintenanceStageObject = (area) => {
      return {
        area,
        allTraps: 0,
        withoutMaintenanceStage: 0,
        firstStage: 0,
        secondStage: 0,
        thirdStage: 0,
        fourthStage: 0,
        fifthStage: 0,
        sixthStage: 0,
        seventhStage: 0,
        eighthStage: 0,
        ninthStage: 0,
        tenthStage: 0,
        beyondTenthMaintenanceStage: 0,
      };
    };

    territorializationsList.forEach(({ label }) => {
      maintenanceStageByAreas[label] = generateMaintenanceStageObject(label);
    });

    const maintenanceStagesMap = {
      0: "withoutMaintenanceStage",
      1: "firstStage",
      2: "secondStage",
      3: "thirdStage",
      4: "fourthStage",
      5: "fifthStage",
      6: "sixthStage",
      7: "seventhStage",
      8: "eighthStage",
      9: "ninthStage",
      10: "tenthStage",
    };

    for (let i = 0; i < trapsList.length; i++) {
      const {
        howManyTimesWasReaded,
        territorializationName,
        status,
        trapType,
      } = trapsList[i];

      if (
        status !== ACTIVE_TRAP_STATUS ||
        trapType.name !== appliedFilters.trapType.trapTypeName
      )
        continue;

      if (maintenanceStageByAreas[territorializationName]) {
        maintenanceStageByAreas[territorializationName].allTraps++;

        const maintenanceStage = maintenanceStagesMap[howManyTimesWasReaded];

        if (maintenanceStage && howManyTimesWasReaded <= 10) {
          maintenanceStageByAreas[territorializationName][maintenanceStage]++;

          continue;
        }

        maintenanceStageByAreas[territorializationName][
          "beyondTenthMaintenanceStage"
        ]++;
      }
    }

    return Array.from(Object.values(maintenanceStageByAreas));
  };

  const calculateMaintenanceStageByAreasCumulative = (
    trapsList,
    territorializationsList
  ) => {
    const maintenanceStageByAreas = {};

    const generateMaintenanceStageObject = (area) => ({
      area,
      allTraps: 0,
      withoutMaintenanceStage: 0,
      firstStage: 0,
      secondStage: 0,
      thirdStage: 0,
      fourthStage: 0,
      fifthStage: 0,
      sixthStage: 0,
      seventhStage: 0,
      eighthStage: 0,
      ninthStage: 0,
      tenthStage: 0,
      beyondTenthMaintenanceStage: 0,
    });

    // Inicializa os objetos para cada polígono (área)
    territorializationsList.forEach(({ label }) => {
      maintenanceStageByAreas[label] = generateMaintenanceStageObject(label);
    });

    // Função auxiliar para mapear o número da manutenção para o nome da propriedade
    const getStageName = (stage) => {
      const stages = [
        "firstStage",
        "secondStage",
        "thirdStage",
        "fourthStage",
        "fifthStage",
        "sixthStage",
        "seventhStage",
        "eighthStage",
        "ninthStage",
        "tenthStage",
      ];
      return stages[stage - 1];
    };

    // Percorre a lista de armadilhas
    for (let i = 0; i < trapsList.length; i++) {
      const {
        howManyTimesWasReaded,
        territorializationName,
        status,
        trapType,
      } = trapsList[i];

      // Filtra armadilhas pelo status e tipo desejado
      if (
        status !== ACTIVE_TRAP_STATUS ||
        trapType.name !== appliedFilters.trapType.trapTypeName
      )
        continue;

      // Verifica se o polígono existe no objeto (caso a armadilha pertença a um polígono esperado)
      if (maintenanceStageByAreas[territorializationName]) {
        maintenanceStageByAreas[territorializationName].allTraps++;

        // Se a armadilha não recebeu manutenção nenhuma
        if (howManyTimesWasReaded === 0) {
          maintenanceStageByAreas[territorializationName]
            .withoutMaintenanceStage++;
        } else {
          // Para cada manutenção acumulada, incrementa o contador correspondente
          // Por exemplo, se recebeu 3 manutenções, incrementa firstStage, secondStage e thirdStage
          const maxStage = Math.min(howManyTimesWasReaded, 10);
          for (let stage = 1; stage <= maxStage; stage++) {
            const stageName = getStageName(stage);
            maintenanceStageByAreas[territorializationName][stageName]++;
          }

          // Se a manutenção for além da 10ª, incrementa a contagem de beyondTenthMaintenanceStage
          if (howManyTimesWasReaded > 10) {
            maintenanceStageByAreas[territorializationName]
              .beyondTenthMaintenanceStage++;
          }
        }
      }
    }

    // Retorna um array com os objetos de cada polígono
    return Array.from(Object.values(maintenanceStageByAreas));
  };

  const handleDownloadHistogramTable = () => {
    const xlsxFileSettings = {
      fileName: `armadilhas_estagio_manutencao_VITEC_${moment().format(
        "DD/MM/YYYY HH:mm"
      )}`,
      extraLength: 3,
      writeMode: "writeFile",
      writeOptions: {},
      RTL: false,
    };

    xlsx(
      [
        {
          sheet: "Estágio de manutenção",
          columns: tableColumns.map(({ name, selector }) => ({
            label: name,
            value: selector,
          })),
          content: maintenanceStageByTerritorializations,
        },
      ],
      xlsxFileSettings
    );
  };

  const tableTypesOptions = [
    { label: "Exclusivo", value: "exclusive" },
    { label: "Acumulativo", value: "cumulative" },
  ];

  const tableColumns = [
    {
      name: "Área",
      selector: "area",
      sortable: true,
    },
    {
      name: "Total de armadilhas",
      selector: "allTraps",
      sortable: true,
    },
    {
      name: "Sem manutenção",
      selector: "withoutMaintenanceStage",
      sortable: true,
    },
    {
      name: "1ª manutenção",
      selector: "firstStage",
      sortable: true,
    },
    {
      name: "2ª manutenção",
      selector: "secondStage",
      sortable: true,
    },
    {
      name: "3ª manutenção",
      selector: "thirdStage",
      sortable: true,
    },
    {
      name: "4ª manutenção",
      selector: "fourthStage",
      sortable: true,
    },
    {
      name: "5ª manutenção",
      selector: "fifthStage",
      sortable: true,
    },
    {
      name: "6ª manutenção",
      selector: "sixthStage",
      sortable: true,
    },
    {
      name: "7ª manutenção",
      selector: "seventhStage",
      sortable: true,
    },
    {
      name: "8ª manutenção",
      selector: "eighthStage",
      sortable: true,
    },
    {
      name: "9ª manutenção",
      selector: "ninthStage",
      sortable: true,
    },
    {
      name: "10ª manutenção",
      selector: "tenthStage",
      sortable: true,
    },
    {
      name: "Acima da 10ª manutenção",
      selector: "beyondTenthMaintenanceStage",
      sortable: true,
    },
  ];

  return {
    tableTypesOptions,
    selectedTableType,
    setSelectedTableType,
    maintenanceStageCumulativeByTerritorializations,
    maintenanceStageByTerritorializations,
    appliedFilters,
    tableColumns,
    handleDownloadHistogramTable,
    isTrapsListLoading,
  };
};

export const TrapsMaintenanceStageByTerritorializationsTable = ({
  trapsList,
}) => {
  const {
    tableTypesOptions,
    selectedTableType,
    setSelectedTableType,
    maintenanceStageCumulativeByTerritorializations,
    maintenanceStageByTerritorializations,
    appliedFilters,
    tableColumns,
    handleDownloadHistogramTable,
    isTrapsListLoading,
  } = useTrapsMaintenanceStageByTerritorializationsTable(trapsList);

  if (
    appliedFilters.trapType.trapTypeName !==
      "armadilhaDisseminadoraInseticida" ||
    appliedFilters.territorializations.length === 0 ||
    appliedFilters.filtersTypes.territorializationsIds === 2
  )
    return null;

  if (isTrapsListLoading) return <TableLoadingSkeleton variant={"simple"} />;

  if (selectedTableType.value === "exclusive")
    return (
      <>
        <Card className="mb-4">
          <CardHeader
            headerText={
              <span>
                Tabela de estágio de manutenção por áreas (
                {ARMADILHA_INFO[appliedFilters.trapType.trapTypeName].apelido})
              </span>
            }
            showExpandButton={false}
            showButtonsTooltips={true}
            showDownloadDataButton={false}
          >
            <Button
              onClick={() => handleDownloadHistogramTable()}
              color="primary"
              data-tip
              data-for="dataDownload"
              style={{ width: "55px", marginRight: "0px" }}
            >
              <i className="fa fa-download" />
            </Button>
            <ReactTooltip effect="solid" type="info" id="dataDownload">
              Baixar dados
            </ReactTooltip>
            <div style={{ width: "170px" }}>
              <Select
                styles={selectComponentStyles}
                placeholder={"Selecione"}
                isMulti={false}
                blurInputOnSelect={false}
                closeMenuOnSelect={false}
                isClearable={false}
                value={selectedTableType}
                options={tableTypesOptions}
                onChange={(e) => setSelectedTableType(e)}
              />
            </div>
            <CircleHelp
              size={17}
              data-tip
              data-for="tableTypeHelperExclusive"
              style={{ opacity: 0.7 }}
            />
            <ReactTooltip
              effect="solid"
              type="info"
              id="tableTypeHelperExclusive"
            >
              Ex.: Se a armadilha recebeu um total de cinco manutenções
              incrementará somente os valores na coluna da quinta manutenção.
            </ReactTooltip>
          </CardHeader>
          <CardContent>
            <Row>
              <Col>
                <DataTable
                  noHeader
                  defaultSortField="name"
                  defaultSortAsc={true}
                  pagination
                  highlightOnHover
                  responsive
                  columns={tableColumns}
                  data={maintenanceStageByTerritorializations}
                  noDataComponent={"Nenhum registro encontrado."}
                />
              </Col>
            </Row>
          </CardContent>
          <CardFooter>
            <div></div>
            <img
              style={{ maxWidth: "100px", opacity: 0.5 }}
              src="/static/media/vitec.9bd71d52.png"
              alt="Logo do Vitec"
            />
          </CardFooter>
        </Card>
      </>
    );

  if (selectedTableType.value === "cumulative")
    return (
      <>
        <Card className="mb-4">
          <CardHeader
            headerText={
              <span>
                Tabela de estágio de manutenção por áreas (
                {ARMADILHA_INFO[appliedFilters.trapType.trapTypeName].apelido})
              </span>
            }
            showExpandButton={false}
            showButtonsTooltips={true}
            showDownloadDataButton={false}
          >
            <Button
              onClick={() => handleDownloadHistogramTable()}
              color="primary"
              data-tip
              data-for="dataDownload"
              style={{ width: "55px", marginRight: "0px" }}
            >
              <i className="fa fa-download" />
            </Button>
            <ReactTooltip effect="solid" type="info" id="dataDownload">
              Baixar dados
            </ReactTooltip>
            <div style={{ width: "170px" }}>
              <Select
                styles={selectComponentStyles}
                placeholder={"Selecione"}
                isMulti={false}
                blurInputOnSelect={false}
                closeMenuOnSelect={false}
                isClearable={false}
                value={selectedTableType}
                options={tableTypesOptions}
                onChange={(e) => setSelectedTableType(e)}
              />
            </div>
            <CircleHelp
              size={17}
              data-tip
              data-for="tableTypeHelperCumulative"
              style={{ opacity: 0.7 }}
            />
            <ReactTooltip
              effect="solid"
              type="info"
              id="tableTypeHelperCumulative"
            >
              Ex.: Se a armadilha recebeu três manutenções logo incrementará os
              valores das colunas de primeira, segunda e terceira manutenção.
            </ReactTooltip>
          </CardHeader>
          <CardContent>
            <Row>
              <Col>
                <DataTable
                  noHeader
                  defaultSortField="name"
                  defaultSortAsc={true}
                  pagination
                  highlightOnHover
                  responsive
                  columns={tableColumns}
                  data={maintenanceStageCumulativeByTerritorializations}
                  noDataComponent={"Nenhum registro encontrado."}
                />
              </Col>
            </Row>
          </CardContent>
          <CardFooter>
            <div></div>
            <img
              style={{ maxWidth: "100px", opacity: 0.5 }}
              src="/static/media/vitec.9bd71d52.png"
              alt="Logo do Vitec"
            />
          </CardFooter>
        </Card>
      </>
    );

  return null;
};
